// pages/admin/AdminUsers.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import AdminModal from './components/AdminModal';
import AdminConfirmDialog from './components/AdminConfirmDialog';
import AdminPagination from './components/AdminPagination';

const AdminUsers = () => {
  const { apiCall, addNotification } = useAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, user: null, action: '' });
  const [editingPayment, setEditingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    subscriptionType: 'Basic',
    subscriptionStatus: '',
    billingFrequency: '',
    currentPlanAmount: '',
    nextPayDate: ''
  });

  const limit = 10;

  // Debounce search function
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedSearch = useCallback(
    debounce((term) => {
      setCurrentPage(1);
      loadUsers(1, term, statusFilter, typeFilter);
    }, 500),
    [statusFilter, typeFilter]
  );

  useEffect(() => {
    loadUsers();
  }, [currentPage, statusFilter, typeFilter]);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const loadUsers = async (page = currentPage, search = searchTerm, status = statusFilter, type = typeFilter) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...(search && { search }),
        ...(status && { status }),
        ...(type && { type })
      });

      const response = await apiCall(
        `https://phenixloc-api.fnvt.net/api/admin/users?${queryParams.toString()}`
      );
      
      setUsers(response.users);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalUsers(response.pagination.total);

    } catch (error) {
      console.error('Error loading users:', error);
      addNotification('Error loading users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getAccountStatusBadge = (accountStatus) => {
  switch (accountStatus) {
    case 'Active':
      return <span className="admin-badge admin-badge-active">Active</span>;
    case 'Frozen':
      return <span className="admin-badge admin-badge-warning">Frozen</span>;
    case 'Trial':
      return <span className="admin-badge admin-badge-info">Trial</span>;
    default:
      return <span className="admin-badge admin-badge-secondary">{accountStatus}</span>;
  }
};

  const viewUserProfile = async (userId) => {
    try {
      const response = await apiCall(`https://phenixloc-api.fnvt.net/api/admin/users/${userId}`);
      setSelectedUser(response.user);
      setShowUserModal(true);
      
      // Initialize payment form with current data
      if (response.user.paymentDetails) {
        setPaymentForm({
          subscriptionType: response.user.userType || 'Basic', // Use main userType (now consistent)
          subscriptionStatus: response.user.subscriptionStatus || '',
          billingFrequency: response.user.paymentDetails.billingFrequency || '',
          currentPlanAmount: response.user.paymentDetails.currentPlanAmount || '',
          nextPayDate: response.user.paymentDetails.nextPayDate ? 
            new Date(response.user.paymentDetails.nextPayDate).toISOString().split('T')[0] : ''
        });
      } else {
        setPaymentForm({
          subscriptionType: response.user.userType || 'Basic',
          subscriptionStatus: response.user.subscriptionStatus || '',
          billingFrequency: '',
          currentPlanAmount: '',
          nextPayDate: ''
        });
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      addNotification('Error loading user details', 'error');
    }
  };

  const handleUserAction = (user, action) => {
    setConfirmDialog({
      show: true,
      user,
      action,
      title: getActionTitle(action),
      message: getActionMessage(user, action),
      confirmText: getActionButtonText(action),
      confirmClass: getActionButtonClass(action)
    });
  };

  const confirmUserAction = async () => {
    const { user, action } = confirmDialog;
    
    try {
      await apiCall('https://phenixloc-api.fnvt.net/api/admin/users/action', {
        method: 'POST',
        body: JSON.stringify({
          userId: user.id,
          action
        })
      });

      const actionPast = {
        'block': 'blocked',
        'unblock': 'unblocked',
        'delete': 'deleted',
        'restore': 'restored'
      }[action];

      addNotification(`User ${actionPast} successfully.`, 'success');
      loadUsers();
      setConfirmDialog({ show: false, user: null, action: '' });

    } catch (error) {
      console.error(`Error performing user action (${action}):`, error);
      addNotification(`Error: ${error.message}`, 'error');
    }
  };

  const handlePaymentUpdate = async () => {
    try {
      await apiCall(`https://phenixloc-api.fnvt.net/api/admin/users/${selectedUser.id}/payment`, {
        method: 'PUT',
        body: JSON.stringify(paymentForm)
      });

      addNotification('Payment details updated successfully', 'success');
      setEditingPayment(false);
      
      // Refresh user data
      viewUserProfile(selectedUser.id);
      
    } catch (error) {
      console.error('Error updating payment details:', error);
      addNotification(`Error updating payment details: ${error.message}`, 'error');
    }
  };

  const getActionTitle = (action) => {
    const titles = {
      'block': 'Block User',
      'unblock': 'Unblock User',
      'delete': 'Delete User',
      'restore': 'Restore User'
    };
    return titles[action] || 'Confirm Action';
  };

  const getActionMessage = (user, action) => {
    const messages = {
      'block': `Are you sure you want to block ${user.name || user.email}? They will not be able to login until unblocked.`,
      'unblock': `Are you sure you want to unblock ${user.name || user.email}? They will regain access to their account.`,
      'delete': `Are you sure you want to delete ${user.name || user.email}? This action can be reversed later.`,
      'restore': `Are you sure you want to restore ${user.name || user.email}? Their account will be reactivated.`
    };
    return messages[action] || 'Are you sure you want to proceed?';
  };

  const getActionButtonText = (action) => {
    const texts = {
      'block': 'Block User',
      'unblock': 'Unblock User',
      'delete': 'Delete User',
      'restore': 'Restore User'
    };
    return texts[action] || 'Confirm';
  };

  const getActionButtonClass = (action) => {
    const classes = {
      'block': 'admin-btn-warning',
      'unblock': 'admin-btn-success',
      'delete': 'admin-btn-danger',
      'restore': 'admin-btn-success'
    };
    return classes[action] || 'admin-btn-primary';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="admin-badge admin-badge-active">Active</span>;
      case 'blocked':
        return <span className="admin-badge admin-badge-warning">Blocked</span>;
      case 'deleted':
        return <span className="admin-badge admin-badge-danger">Deleted</span>;
      default:
        return <span className="admin-badge admin-badge-secondary">Unknown</span>;
    }
  };

  const getUserTypeBadge = (userType) => {
    switch (userType) {
      case 'Plus':
        return <span className="admin-badge admin-badge-premium">Plus</span>;
      case 'Trial':
        return <span className="admin-badge admin-badge-trial">Trial</span>;
      case 'Basic':
      default:
        return <span className="admin-badge admin-badge-basic">Basic</span>;
    }
  };

  const getSubscriptionStatusBadge = (status) => {
  // Handle null/undefined/empty values
  if (!status || status === null || status === undefined || status === '') {
    return <span className="admin-badge admin-badge-secondary">No Subscription</span>;
  }
  
  switch (status) {
    case 'Active':
      return <span className="admin-badge admin-badge-active">Active</span>;
    case 'Expired':
      return <span className="admin-badge admin-badge-danger">Expired</span>;
    case 'Deleted':
      return <span className="admin-badge admin-badge-danger">Deleted</span>;
    default:
      return <span className="admin-badge admin-badge-secondary">{status}</span>;
  }
};

  const getActionButtons = (user) => {
    switch (user.status) {
      case 'active':
        return (
          <>
            <button 
              className="admin-btn-icon admin-btn-view" 
              title="View Profile"
              onClick={() => viewUserProfile(user.id)}
            >
              <i className="fas fa-eye"></i>
            </button>
            <button 
              className="admin-btn-icon admin-btn-warning" 
              title="Block User"
              onClick={() => handleUserAction(user, 'block')}
            >
              <i className="fas fa-ban"></i>
            </button>
            <button 
              className="admin-btn-icon admin-btn-danger" 
              title="Delete User"
              onClick={() => handleUserAction(user, 'delete')}
            >
              <i className="fas fa-trash"></i>
            </button>
          </>
        );
      case 'blocked':
        return (
          <>
            <button 
              className="admin-btn-icon admin-btn-view" 
              title="View Profile"
              onClick={() => viewUserProfile(user.id)}
            >
              <i className="fas fa-eye"></i>
            </button>
            <button 
              className="admin-btn-icon admin-btn-success" 
              title="Unblock User"
              onClick={() => handleUserAction(user, 'unblock')}
            >
              <i className="fas fa-check-circle"></i>
            </button>
            <button 
              className="admin-btn-icon admin-btn-danger" 
              title="Delete User"
              onClick={() => handleUserAction(user, 'delete')}
            >
              <i className="fas fa-trash"></i>
            </button>
          </>
        );
      case 'deleted':
        return (
          <>
            <button 
              className="admin-btn-icon admin-btn-view" 
              title="View Profile"
              onClick={() => viewUserProfile(user.id)}
            >
              <i className="fas fa-eye"></i>
            </button>
            <button 
              className="admin-btn-icon admin-btn-success" 
              title="Restore User"
              onClick={() => handleUserAction(user, 'restore')}
            >
              <i className="fas fa-trash-restore"></i>
            </button>
          </>
        );
      default:
        return (
          <button 
            className="admin-btn-icon admin-btn-view" 
            title="View Profile"
            onClick={() => viewUserProfile(user.id)}
          >
            <i className="fas fa-eye"></i>
          </button>
        );
    }
  };

  return (
    <div className="admin-users">
      <div className="admin-page-header">
        <h2>Users Management</h2>
        <p>Manage PhenixLoc users</p>
      </div>

      <div className="admin-filter-bar">
        <div className="admin-search-filter">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="fas fa-search"></i>
        </div>
        <div className="admin-action-filters">
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Status: All</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
            <option value="deleted">Deleted</option>
          </select>
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Type: All</option>
            <option value="basic">Basic</option>
            <option value="plus">Plus</option>
            <option value="trial">Trial</option>
          </select>
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username / Email</th>
              <th>Creation Date</th>
              <th>Subscription</th>
<th>Account Status</th>
<th>Sub Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="admin-loading-data">
                  <p>Loading user data...</p>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="5" className="admin-empty-data">
                  <p>No users found. Try adjusting your filters.</p>
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="admin-user-info">
                      <div className="admin-user-avatar">
                        <i className="fas fa-user"></i>
                      </div>
                      <div className="admin-user-details">
                        <p className="admin-user-name">{user.name || 'Unnamed User'}</p>
                        <p className="admin-user-email">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className='text-black'>
                    {new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td>{getUserTypeBadge(user.userType)}</td>
<td>{getAccountStatusBadge(user.accountStatus)}</td>
<td className='text-black'>{getSubscriptionStatusBadge(user.subscriptionStatus)}</td>
                  <td>
                    <div className="admin-action-buttons">
                      {getActionButtons(user)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* User Profile Modal */}
      <AdminModal
        show={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setEditingPayment(false);
        }}
        title="User Profile"
        size="large"
      >
        {selectedUser && (
          <div className="admin-user-profile">
            <div className="admin-user-profile-header">
              <div className="admin-profile-avatar">
                <i className="fas fa-user-circle"></i>
              </div>
              <div className="admin-profile-info">
                <h4>{selectedUser.fullName || 'Unnamed User'}</h4>
                <p>{selectedUser.email}</p>
                <div className="admin-profile-badges">
  {getUserTypeBadge(selectedUser.userType)}
  {getAccountStatusBadge(selectedUser.accountStatus)}
  {getSubscriptionStatusBadge(selectedUser.subscriptionStatus)}
</div>
              </div>
            </div>

            <div className="admin-profile-section">
              <h4>Account Information</h4>
              <div className="admin-profile-data">
                <div className="admin-data-item">
                  <span className="admin-data-label">Member Since:</span>
                  <span className="admin-data-value">
                    {new Date(selectedUser.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="admin-data-item">
                  <span className="admin-data-label">Last Login:</span>
                  <span className="admin-data-value">
                    {selectedUser.lastLogin 
                      ? new Date(selectedUser.lastLogin).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Never'
                    }
                  </span>
                </div>
                <div className="admin-data-item">
                  <span className="admin-data-label">Subscription Type:</span>
                  <span className="admin-data-value">{selectedUser.userType || 'Basic'}</span>
                </div>
              </div>
            </div>

            {/* Payment Information Section */}
            <div className="admin-profile-section">
              <div className="admin-section-header">
                <h4>Payment Information</h4>
                {!editingPayment && (
                  <button 
                    className="admin-btn admin-btn-primary admin-btn-sm"
                    onClick={() => setEditingPayment(true)}
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                )}
              </div>

              {editingPayment ? (
                <div className="admin-payment-edit-form">
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Subscription Type</label>
                      <select
                        value={paymentForm.subscriptionType}
                        onChange={(e) => setPaymentForm({...paymentForm, subscriptionType: e.target.value})}
                      >
                        <option value="">Select Type</option>
                        <option value="Basic">Basic</option>
                        <option value="Plus">Plus</option>
                        <option value="Trial">Trial</option>
                      </select>
                    </div>
                    <div className="admin-form-group">
                      <label>Subscription Status</label>
                      <select
  value={paymentForm.subscriptionStatus}
  onChange={(e) => setPaymentForm({...paymentForm, subscriptionStatus: e.target.value})}
>
  <option value="">Select Status</option>
  <option value="Active">Active</option>
  <option value="Expired">Expired</option>
  <option value="Deleted">Deleted</option>
</select>
                    </div>
                  </div>
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Billing Frequency</label>
                      <select
                        value={paymentForm.billingFrequency}
                        onChange={(e) => setPaymentForm({...paymentForm, billingFrequency: e.target.value})}
                      >
                        <option value="">Select Frequency</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div className="admin-form-group">
                      <label>Current Plan Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={paymentForm.currentPlanAmount}
                        onChange={(e) => setPaymentForm({...paymentForm, currentPlanAmount: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Next Payment Date</label>
                      <input
                        type="date"
                        value={paymentForm.nextPayDate}
                        onChange={(e) => setPaymentForm({...paymentForm, nextPayDate: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="admin-form-actions">
                    <button 
                      className="admin-btn admin-btn-success"
                      onClick={handlePaymentUpdate}
                    >
                      Save Changes
                    </button>
                    <button 
                      className="admin-btn admin-btn-secondary"
                      onClick={() => setEditingPayment(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="admin-payment-details">
                  {selectedUser.paymentDetails ? (
                    <div className="admin-profile-data">
                      <div className="admin-data-item">
                        <span className="admin-data-label">Subscription Type:</span>
                        <span className="admin-data-value">{selectedUser.userType || 'Basic'}</span>
                      </div>
                      <div className="admin-data-item">
                        <span className="admin-data-label">Status:</span>
                        <span className="admin-data-value">
                          {getSubscriptionStatusBadge(selectedUser.subscriptionStatus)}
                        </span>
                      </div>
                      <div className="admin-data-item">
                        <span className="admin-data-label">Billing Frequency:</span>
                        <span className="admin-data-value">{selectedUser.paymentDetails.billingFrequency || 'None'}</span>
                      </div>
                      <div className="admin-data-item">
                        <span className="admin-data-label">Current Plan Amount:</span>
                        <span className="admin-data-value">
                          ${selectedUser.paymentDetails.currentPlanAmount || '0.00'}
                        </span>
                      </div>
                      <div className="admin-data-item">
                        <span className="admin-data-label">Last Paid:</span>
                        <span className="admin-data-value">
                          {selectedUser.paymentDetails.lastPaidDate 
                            ? new Date(selectedUser.paymentDetails.lastPaidDate).toLocaleDateString()
                            : 'Never'
                          }
                        </span>
                      </div>
                      <div className="admin-data-item">
                        <span className="admin-data-label">Next Payment:</span>
                        <span className="admin-data-value">
                          {selectedUser.paymentDetails.nextPayDate 
                            ? new Date(selectedUser.paymentDetails.nextPayDate).toLocaleDateString()
                            : 'Not set'
                          }
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="admin-no-data">No payment information available</p>
                  )}
                </div>
              )}
            </div>

            {/* Payment History Section */}
            {selectedUser.paymentHistory && selectedUser.paymentHistory.length > 0 && (
              <div className="admin-profile-section">
                <h4>Recent Payment History</h4>
                <div className="admin-payment-history">
                  {selectedUser.paymentHistory.map(payment => (
                    <div key={payment.id} className="admin-payment-item">
                      <div className="admin-payment-info">
                        <span className="admin-payment-date">
                          {new Date(payment.paymentDate).toLocaleDateString()}
                        </span>
                        <span className="admin-payment-amount">
                          ${payment.paymentAmount} {payment.currency}
                        </span>
                        <span className="admin-payment-method">
                          {payment.paymentMethod}
                        </span>
                      </div>
                      <div className="admin-payment-status">
                        <span className={`admin-badge ${payment.paymentStatus === 'completed' ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                          {payment.paymentStatus}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedUser.inheritances && selectedUser.inheritances.length > 0 && (
              <div className="admin-profile-section">
                <h4>Inheritances</h4>
                <div className="admin-profile-inheritances">
                  {selectedUser.inheritances.map(inheritance => (
                    <div key={inheritance.id} className="admin-inheritance-item">
                      <div className="admin-inheritance-header">
                        <span className="admin-inheritance-name">{inheritance.filename}</span>
                        <span className={`admin-badge ${inheritance.isActive ? 'admin-badge-active' : 'admin-badge-warning'}`}>
                          {inheritance.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="admin-inheritance-details">
                        <p>Recipient: {inheritance.recipientEmail || 'None'}</p>
                        <p>Expiration: {inheritance.expirationDate 
                          ? new Date(inheritance.expirationDate).toLocaleDateString() 
                          : 'None'
                        }</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </AdminModal>

      {/* Confirmation Dialog */}
      <AdminConfirmDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmClass={confirmDialog.confirmClass}
        onConfirm={confirmUserAction}
        onCancel={() => setConfirmDialog({ show: false, user: null, action: '' })}
      />
    </div>
  );
};

export default AdminUsers;